import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { Button, Dimmer, Icon, Loader, Modal, Pagination } from 'semantic-ui-react';
import moment from 'moment';
import momentTZLib from 'moment-timezone';
import "./style.less";
import StatusFilters from '../../components/OrderStatus/StatusFilters';
import OrderStatusCard from '../../components/OrderStatus/OrderStatusCard';
import { useFacility } from '../../contexts/facility/FacilityContext';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { fetchOrders } from '../../services/OrderStatus';
import { FetchOrderParams, RequestInstance } from '../../types/OrderStatus';
import CreateServiceOrder from '../../components/OrderStatus/CreateServiceOrder';
import CreateOrderWizardModal from '../../components/OrderStatus/CreateOrderWizardModal';
import { Service } from '../../types/Service';
import { formatOrdersForTableView } from '../../../utils';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';

export interface DateRange {
    fromDate?: Date,
    toDate?: Date;
}

const OrderStatus = () => {
    const [dates, setDates] = useState<DateRange>({
        fromDate: (() => {
            const fromDate = new Date();
            fromDate.setDate(fromDate.getDate() - 7); // Subtract 7 days from the current date
            return fromDate;
        })(),
        toDate: (() => {
            const toDate = new Date();
            toDate.setDate(toDate.getDate() + 7); // Add 7 days to the current date
            return toDate;
        })(),
    });
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const dateFilterRef = useRef<HTMLButtonElement>(null);
    const datePopoverRef = useRef<HTMLDivElement>(null);
    const [popoverCoords, setPopoverCoords] = useState<{ top: number; left: number } | null>(null);
    const { facilityTimezone, loading: facilityLoading } = useFacility();
    const [filterStatus, setFilterStatus] = useState<string>("");
    const [orders, setOrders] = useState<RequestInstance[]>([]);
    const [totalOpenOrders, setTotalOpenOrders] = useState<number>(0);
    const [totalClosedOrders, setTotalClosedOrders] = useState<number>(0);
    const [totalInProgressOrders, setTotalInProgressOrders] = useState<number>(0);
    const [pageNo, setPageNo] = useState<number>(1);
    const [totalDataLength, setTotalDataLength] = useState<number>(0);
    const [openCreateOrderModal, setOpenCreateOrderModal] = useState<boolean>(false);
    const [openCreateOrderWizard, setOpenCreateOrderWizard] = useState<boolean>(false);
    const [selectedService, setSelectedService] = useState<Service>({} as Service);
    const [selectedOrder, setSelectedOrder] = useState<RequestInstance>({} as RequestInstance);
    const [selectedCategory, setSelectedCategory] = useState<string>("");

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only supports the resident who is logged in and we can't deselect or change it
    const pageSize = 20;
    const { resolvedFacilityPaymentsEnabled } = useNeighborsPaymentAccess();

    useEffect(() => {
        if (!showDatePicker) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (dateFilterRef.current && dateFilterRef.current.contains(target)) {
                return;
            }
            if (datePopoverRef.current && datePopoverRef.current.contains(target)) {
                return;
            }
            setShowDatePicker(false);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowDatePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showDatePicker]);

    // Move focus into the popover when it opens (it renders no natively
    // focusable element until a day cell exists), and restore focus to the
    // trigger when it closes so keyboard users aren't dropped at the end of
    // <body>. Skipped on mount so the trigger isn't stolen-focused on load.
    const wasDatePickerOpenRef = useRef(false);
    useEffect(() => {
        if (showDatePicker) {
            datePopoverRef.current?.focus();
        } else if (wasDatePickerOpenRef.current) {
            dateFilterRef.current?.focus();
        }
        wasDatePickerOpenRef.current = showDatePicker;
    }, [showDatePicker]);

    // Semantic UI's `.pushable` applies a transform, which makes `position: fixed`
    // relative to that (page-tall) box instead of the viewport — so a "bottom: 12px"
    // calendar lands thousands of pixels down the order list. Portal to body and
    // pin the popover to the trigger in viewport coordinates.
    useLayoutEffect(() => {
        if (!showDatePicker) {
            setPopoverCoords(null);
            return undefined;
        }

        const placePopover = () => {
            const trigger = dateFilterRef.current;
            const popover = datePopoverRef.current;
            if (!trigger || !popover) {
                return;
            }

            const rect = trigger.getBoundingClientRect();
            const popW = popover.offsetWidth;
            const popH = popover.offsetHeight;
            const margin = 12;
            const gap = 8;
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            let left = rect.left + (rect.width - popW) / 2;
            left = Math.min(Math.max(margin, left), Math.max(margin, vw - popW - margin));

            let top = rect.bottom + gap;
            if (top + popH > vh - margin) {
                top = rect.top - gap - popH;
            }
            if (top < margin) {
                top = margin;
            }

            setPopoverCoords({ top, left });
        };

        placePopover();
        window.addEventListener('resize', placePopover);
        window.addEventListener('scroll', placePopover, true);
        return () => {
            window.removeEventListener('resize', placePopover);
            window.removeEventListener('scroll', placePopover, true);
        };
    }, [showDatePicker]);

    useEffect(() => {
        if (!facilityTimezone) {
            return;
        }
        if (dates.fromDate && dates.toDate) {
            listOrders();
        }
    }, [dates, filterStatus, pageNo, facilityTimezone]);

    const listOrders = async () => {
        if (!facilityTimezone) {
            return;
        }
        const fromDateString = moment(dates.fromDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const toDateString = moment(dates.toDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const orderFilter: FetchOrderParams = {
            facilityId: activeResident.Facility,
            statusArr: [],
            pageNo: pageNo,
            pageSize: pageSize,
            startDateTime: fromDateString,
            endDateTime: toDateString,
            residentId: String(activeResident._id),
        };
        try {
            setLoading(true);
            const [openOrders, inProgressOrders, closedOrders] = await Promise.all([fetchOrders({ ...orderFilter, statusArr: ["Open"] }), fetchOrders({ ...orderFilter, statusArr: ["Accepted"] }), fetchOrders({ ...orderFilter, statusArr: ["Closed"] })]);
            setTotalClosedOrders(closedOrders.Result.Content.TotRecords);
            setTotalOpenOrders(openOrders.Result.Content.TotRecords);
            setTotalInProgressOrders(inProgressOrders.Result.Content.TotRecords);
            if (filterStatus === "Open") {
                const formattedOrders = formatOrdersForTableView(openOrders.Result.Content.Result, facilityTimezone);
                setOrders(formattedOrders);
                setTotalDataLength(openOrders.Result.Content.TotRecords);
            } else if (filterStatus === "Accepted") {
                const formattedOrders = formatOrdersForTableView(inProgressOrders.Result.Content.Result, facilityTimezone);
                setOrders(formattedOrders);
                setTotalDataLength(inProgressOrders.Result.Content.TotRecords);
            }
            else if (filterStatus === "Closed") {
                const formattedOrders = formatOrdersForTableView(closedOrders.Result.Content.Result, facilityTimezone);
                setOrders(formattedOrders);
                setTotalDataLength(closedOrders.Result.Content.TotRecords);
            } else {
                const combinedRes = await fetchOrders({ ...orderFilter, statusArr: ["Open", "Accepted", "Closed"] }); // Extra call to fetch all three combined so that server pagination is proper
                setTotalDataLength(combinedRes.Result.Content.TotRecords);
                const combinedOrders = combinedRes.Result.Content.Result;
                combinedOrders.sort(function (a, b) {
                    const dateA = momentTZLib.tz(a.RequestedTime, facilityTimezone);
                    const dateB = momentTZLib.tz(b.RequestedTime, facilityTimezone);

                    if (dateB.isBefore(dateA)) {
                        return -1; // if b is in the past, put a first
                    } else if (dateA.isBefore(dateB)) {
                        return 1; // if a is in the past, put b first
                    } else {
                        return 0;
                    }
                });
                const formattedOrders = formatOrdersForTableView(combinedOrders, facilityTimezone);
                setOrders(formattedOrders);
            }
        } catch (error) {
            sendToast('error', error);
        }
        finally {
            setLoading(false);
        }
    };

    const formatDateRange = (dateRange: DateRange) => {
        const startDate = dateRange.fromDate ? moment(dateRange.fromDate).format('MM/DD/YYYY') : 'none';
        const endDate = dateRange.toDate ? moment(dateRange.toDate).format('MM/DD/YYYY') : 'none';
        if (!dateRange.toDate || startDate === endDate) return startDate;
        return `${startDate} - ${endDate}`;
    };

    const onOrderClick = (item: any) => {
        if (!item) {
            sendToast('error', 'Order not found');
            return;
        }
        setSelectedOrder(item);
        setSelectedService(item.Service || {} as Service);
        setSelectedCategory(item.Service?.category || item.Category || '');
        setOpenCreateOrderModal(true);
    };

    const totalPages = Math.ceil((totalDataLength || 0) / pageSize);

    return (
        <>
            <div className="order-status-page" style={{ position: 'relative' }}>
                <Dimmer className="order-status-dimmer" active={loading || facilityLoading} inverted>
                    <Loader active={loading || facilityLoading} />
                </Dimmer>
                <div>
                    <div className="order-status-page-header">
                        <div>
                            <div className="order-status-page-title">Order Status</div>
                            <div className="order-status-page-sub">Track and manage your service requests.</div>
                        </div>
                        <Button
                            onClick={() => setOpenCreateOrderWizard(true)}
                            className="create-request-button"
                        >
                            <Icon name="add" />
                            <span className="create-request-label">Create an order</span>
                        </Button>
                    </div>

                    <div className="order-status-filter-bar">
                        <StatusFilters
                            filterStatus={filterStatus}
                            setFilterStatus={setFilterStatus}
                            totalOpenOrders={totalOpenOrders}
                            totalClosedOrders={totalClosedOrders}
                            totalInProgressOrders={totalInProgressOrders}
                            isLoading={loading}
                            setPageNo={setPageNo}
                        />
                        <button
                            type="button"
                            className="order-status-date-filter"
                            ref={dateFilterRef}
                            aria-haspopup="dialog"
                            aria-expanded={showDatePicker}
                            aria-controls="order-status-date-picker-popover"
                            onClick={() => setShowDatePicker((prev) => !prev)}
                        >
                            <Icon name="calendar alternate outline" />
                            <span className="date-range-selector">{formatDateRange(dates)}</span>
                        </button>
                    </div>

                    {showDatePicker && createPortal(
                        <div
                            ref={datePopoverRef}
                            id="order-status-date-picker-popover"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Select date range"
                            tabIndex={-1}
                            className={`order-status-date-picker-popover${popoverCoords ? ' is-placed' : ''}`}
                            style={popoverCoords ? { top: popoverCoords.top, left: popoverCoords.left } : undefined}
                        >
                            <DatePicker
                                selected={dates.fromDate}
                                startDate={dates.fromDate}
                                endDate={dates.toDate}
                                onChange={(startEndDates) => {
                                    const [start, end] = startEndDates;
                                    setDates({ ...dates, fromDate: start, toDate: end });
                                    if (start && end) {
                                        setShowDatePicker(false);
                                    }
                                }}
                                selectsRange
                                inline
                            />
                        </div>,
                        document.body,
                    )}

                    <div className="order-list">
                        {orders && orders.length > 0 ? (
                            orders.map((order) => (
                                <OrderStatusCard
                                    key={order._id}
                                    order={order}
                                    facilityTimezone={facilityTimezone}
                                    onOpen={(item) => onOrderClick(item)}
                                    onCancelled={listOrders}
                                />
                            ))
                        ) : (
                            !loading && !facilityLoading && <div className="order-empty">No orders match these filters.</div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="order-status-pagination">
                            <Pagination
                                totalPages={totalPages}
                                activePage={pageNo}
                                onPageChange={(e, { activePage }) => setPageNo(Number(activePage))}
                                ellipsisItem={null}
                                firstItem={null}
                                lastItem={null}
                                siblingRange={1}
                                boundaryRange={0}
                                size="mini"
                            />
                        </div>
                    )}

                    <Modal open={openCreateOrderModal} onClose={() => setOpenCreateOrderModal(false)} size="tiny" closeIcon className="order-detail-modal">
                        <Modal.Content>
                            <CreateServiceOrder request={selectedOrder} service={selectedService || {} as Service} selectedCategory={selectedCategory} setOpenCreateOrderModal={setOpenCreateOrderModal} refreshData={listOrders}/>
                        </Modal.Content>
                    </Modal>

                    <CreateOrderWizardModal
                        open={openCreateOrderWizard}
                        onClose={() => setOpenCreateOrderWizard(false)}
                        FacilityId={activeResident.Facility}
                        residentId={String(activeResident._id)}
                        refreshData={listOrders}
                        facilityPaymentsEnabled={resolvedFacilityPaymentsEnabled}
                    />
                </div>
            </div>
        </>
    );
};

export default OrderStatus;